import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Entity } from '@backstage/catalog-model';
import { useRouteRef } from '@backstage/core-plugin-api';
import { buildTechDocsURL } from '@backstage/plugin-techdocs-react';
import {
  Button,
  Cell,
  CellText,
  Flex,
  SearchField,
  Table,
  Text,
  useTable,
} from '@backstage/ui';
import type { SortDescriptor } from '@backstage/ui';
import { RiCloseCircleLine } from '@remixicon/react';
import { EN_DASH } from '@internal/backstage-plugin-neo4j-app-common';
import { BadgeTooltip } from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { DOC_TOPIC_ANNOTATION } from '@internal/plugin-neo4j-techdocs-common';
import { LENGTH_ANNOTATION } from '../types';
import { countFittingTags } from '../utils/countFittingTags';
import { createTechdocsReaderPageRef } from '../routes';
import styles from './DocsTable.module.css';

// Every cell holds 1 line (LIST-010): the text cells truncate, and the tag
// chips do not wrap. A cell needs 44px: the BUI cell padding (2 x 12px) plus
// a compact chip (20px). 48px adds 4px of space.
const ROW_HEIGHT_PX = 48;

const DEFAULT_SORT: SortDescriptor = { column: 'name', direction: 'ascending' };

type DocTableItem = {
  id: string;
  title: string;
  topic: string;
  tags: string[];
  duration: string;
  entity: Entity;
};

export type DocsTableProps = {
  entities: Entity[];
  isPending?: boolean;
  /** Shown when the table has no docs, and the filter hides none. */
  emptyState?: ReactNode;
  /** Shows a field above the table that filters the rows by title, topic, and tag. */
  hasFilter?: boolean;
};

/** A table of docs that links each row to its reader page. */
export const DocsTable = ({
  entities,
  isPending = false,
  emptyState,
  hasFilter = false,
}: DocsTableProps) => {
  const getRouteToReaderPage = useRouteRef(createTechdocsReaderPageRef);
  const items = useMemo(() => entities.map(entityToTableItem), [entities]);
  const { tableProps, search } = useTable({
    mode: 'complete',
    // No data while the list loads, so that the table shows its skeleton rows
    // and not the empty state.
    data: isPending ? undefined : items,
    initialSort: DEFAULT_SORT,
    sortFn: sortItems,
    searchFn: filterItems,
    paginationOptions: { type: 'none' },
  });
  const hasNoRows = tableProps.data?.length === 0;
  const getReaderUrl = (item: DocTableItem) =>
    buildTechDocsURL(item.entity, getRouteToReaderPage) ?? '';

  return (
    <div className={styles.root}>
      {hasFilter && (
        <div className={styles.searchRow}>
          <div className={styles.searchField}>
            <SearchField
              aria-label="Filter docs"
              placeholder="Filter by title, topic, or tag"
              value={search.value}
              onChange={search.onChange}
              size="small"
            />
          </div>
        </div>
      )}
      <Table<DocTableItem>
        {...tableProps}
        className={styles.table}
        isPending={isPending}
        emptyState={
          search.value ? (
            <FilterEmptyState onClear={() => search.onChange('')} />
          ) : (
            emptyState
          )
        }
        // The virtualizer gives the empty state a fixed height, which clips the
        // "Clear filter" button. With no rows there is nothing to virtualize,
        // so the empty table is a plain table that grows with its content.
        virtualized={hasNoRows ? undefined : { rowHeight: ROW_HEIGHT_PX }}
        rowConfig={{ getHref: getReaderUrl }}
        columnConfig={[
          {
            id: 'name',
            label: 'Name',
            isSortable: true,
            isRowHeader: true,
            // Name and Tags share the free width, so that a short title does
            // not leave a wide gap, and the tags truncate less.
            width: '1fr',
            minWidth: 240,
            // A real link in the cell, so that the reader can open the doc in
            // a new tab. The row link (getHref) is a `tr` with a click handler.
            cell: item => (
              <CellText
                title={item.title}
                href={getReaderUrl(item)}
                className={styles.titleCell}
              />
            ),
          },
          {
            id: 'topic',
            label: 'Topic',
            isSortable: true,
            width: 150,
            cell: item => (
              <CellText title={item.topic || EN_DASH} color="secondary" />
            ),
          },
          {
            id: 'tags',
            label: 'Tags',
            isSortable: false,
            width: '1fr',
            minWidth: 200,
            cell: item => (
              <Cell>
                {/* The key measures the chips again when a recycled row
                    shows the tags of another doc. */}
                <TagsCellContent key={item.tags.join('\n')} tags={item.tags} />
              </Cell>
            ),
          },
          {
            id: 'duration',
            label: 'Duration',
            isSortable: true,
            width: 120,
            cell: item => (
              <CellText title={item.duration || EN_DASH} color="secondary" />
            ),
          },
        ]}
      />
    </div>
  );
};

/**
 * The empty state when the filter hides every doc (STATE-004). The icon is the
 * clear icon of the BUI `SearchField`.
 */
function FilterEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Flex align="center" gap="3">
      <Text variant="body-medium">No docs match the filter.</Text>
      <Button
        variant="secondary"
        size="small"
        iconStart={<RiCloseCircleLine />}
        onPress={onClear}
      >
        Clear filter
      </Button>
    </Flex>
  );
}

type ChipWidths = {
  tags: number[];
  overflowChip: number;
  gap: number;
};

/**
 * The tags of a doc on 1 line: the tags that fit, and a "+N" chip with the
 * other tags in a tooltip.
 */
function TagsCellContent({ tags }: { tags: string[] }) {
  const { rowRef, visibleTagCount } = useFittingTagCount(tags);

  if (tags.length === 0) {
    return <Text color="secondary">{EN_DASH}</Text>;
  }

  // Before the measurement, the row holds every tag and a "+N" chip, so that
  // the hook can measure them. The layout effect measures before the browser
  // paints, so the reader does not see this row.
  if (visibleTagCount === undefined) {
    return (
      <Flex ref={rowRef} align="center" gap="1" className={styles.tags}>
        {tags.map(tag => (
          <BadgeTooltip key={tag} size="compact">
            {tag}
          </BadgeTooltip>
        ))}
        <BadgeTooltip size="compact" className={styles.overflowChip}>
          +{tags.length}
        </BadgeTooltip>
      </Flex>
    );
  }

  const visibleTags = tags.slice(0, visibleTagCount);
  const hiddenTags = tags.slice(visibleTagCount);

  return (
    <Flex ref={rowRef} align="center" gap="1" className={styles.tags}>
      {visibleTags.map(tag => (
        <BadgeTooltip key={tag} size="compact">
          {tag}
        </BadgeTooltip>
      ))}
      {hiddenTags.length > 0 && (
        <BadgeTooltip
          size="compact"
          aria-label={`${hiddenTags.length} more tags`}
          // The row is a link. A click or Enter on the chip opens its tooltip
          // and does not open the doc.
          onClick={event => event.stopPropagation()}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          className={styles.overflowChip}
          tooltip={
            <Flex gap="1" className={styles.tooltipTags}>
              {hiddenTags.map(tag => (
                <BadgeTooltip key={tag} size="compact">
                  {tag}
                </BadgeTooltip>
              ))}
            </Flex>
          }
        >
          +{hiddenTags.length}
        </BadgeTooltip>
      )}
    </Flex>
  );
}

/**
 * Measures the tag chips once, then counts the tags that fit in the width of
 * the cell. The count follows the cell when the column or the window resizes.
 * `undefined` until the chips are measured.
 */
function useFittingTagCount(tags: string[]) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [chipWidths, setChipWidths] = useState<ChipWidths>();
  const [availableWidth, setAvailableWidth] = useState(0);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || chipWidths) {
      return;
    }
    // The tag chips, then the "+N" chip.
    const widths = Array.from(row.children, chip =>
      chip instanceof HTMLElement ? chip.offsetWidth : 0,
    );
    setChipWidths({
      tags: widths.slice(0, tags.length),
      overflowChip: widths[tags.length] ?? 0,
      gap: parseFloat(getComputedStyle(row).columnGap) || 0,
    });
    setAvailableWidth(row.clientWidth);
  }, [chipWidths, tags.length]);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() =>
      setAvailableWidth(row.clientWidth),
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  return {
    rowRef,
    visibleTagCount:
      chipWidths &&
      countFittingTags({
        tagWidths: chipWidths.tags,
        availableWidth,
        overflowChipWidth: chipWidths.overflowChip,
        gap: chipWidths.gap,
      }),
  };
}

function entityToTableItem(entity: Entity): DocTableItem {
  return {
    id: entity.metadata.uid ?? entity.metadata.name,
    title: entity.metadata.title ?? entity.metadata.name,
    topic: entity.metadata.annotations?.[DOC_TOPIC_ANNOTATION] ?? '',
    tags: entity.metadata.tags ?? [],
    duration: entity.metadata.annotations?.[LENGTH_ANNOTATION] ?? '',
    entity,
  };
}

function filterItems(items: DocTableItem[], search: string): DocTableItem[] {
  const term = search.toLowerCase();
  return items.filter(item =>
    [item.title, item.topic, ...item.tags].some(text =>
      text.toLowerCase().includes(term),
    ),
  );
}

function parseDurationMinutes(duration: string): number {
  const lower = duration.toLowerCase().trim();
  const num = parseFloat(lower);
  if (isNaN(num)) return 0;
  if (lower.includes('hour')) return num * 60;
  if (lower.includes('week')) return num * 60 * 24 * 7;
  if (lower.includes('day')) return num * 60 * 24;
  return num; // assume minutes
}

function sortItems(
  items: DocTableItem[],
  { column, direction }: SortDescriptor,
): DocTableItem[] {
  return [...items].sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case 'name':
        cmp = a.title.localeCompare(b.title);
        break;
      case 'topic':
        cmp = a.topic.localeCompare(b.topic);
        break;
      case 'duration':
        cmp =
          parseDurationMinutes(a.duration) - parseDurationMinutes(b.duration);
        break;
      default:
        return 0;
    }
    return direction === 'descending' ? -cmp : cmp;
  });
}
